<script setup>

import { useRoute, useRouter } from "vue-router";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import { useWebsiteStore } from "@/stores/website";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { gql } from "graphql-tag";
import { useQuery } from "@vue/apollo-composable";
import Spinner from "@/components/Spinner.vue";
import { useUserStore } from "@/stores/user";
import CrossReferenceInfoBox from "@/views/preview/sections/CrossReferenceInfoBox.vue";

const collapseRef = ref(null)
const issuesAccordionId = `ai-issue-list-accordion-${Math.random().toString(36).slice(2)}`
const websiteStore = useWebsiteStore();
const { website, ignoredIssues} = storeToRefs(websiteStore);

const activeDeviceRender = useActiveDeviceRenderStore();
const router = useRouter()
const route = useRoute()
const props = defineProps({
  devicePreview: Object,
  title: String,
  icon: {
    type: String,
    default: 'fa fa-magnifying-glass-chart'
  },
  categories: {
    type: Array,
    default: [
      'text_quality.spelling',
      'text_quality.grammar',
      'text_quality.readability',
      'text_quality.tone',
      'text_quality.capitalization',
    ]
  },
});

// ----------
const annotations = ref([])
const status = ref("new")
const processedData = ref([])
const matchedIssues = ref([])
const issueFilter = ref('all')
const SELECT_CHAT = gql`
  query Websites($id: bigint!) {
    websites_website(where: {deleted: {_eq: false}, id: {_eq:$id}}){
      id
      url
      crossreferencechecks(where: {status: {_eq: "completed"}}, order_by: {created_at: desc}, limit: 1){
        id
        status
        created_at
        processed_data
      }
    }
  }`

const {result, refetch} = useQuery(SELECT_CHAT, website, () => ({
      // enabled: !!website.value.id,
    })
)

const feedbackGenerated = ref(false);
const aiProgress = ref(0.0)

let allAnnotations = [];
let oldJsonData = ''

function setAnnotations(scroll = true) {
  setTimeout(() => {
    annotations.value = allAnnotations
    showBoxes(scroll)
    highlightSelectedFeedback()
    // let issue = getIssueFromBoxId(route.query.i)
    openIssue(route.query.i)
  }, 100)
}

watch(
    result,
    data => {
      if (!data) {
        return
      }

      feedbackGenerated.value = true
      let jsonData = data.websites_website[0].crossreferencechecks[0].processed_data;
      status.value = data.websites_website[0].crossreferencechecks[0].status;
      const processed = jsonData ? JSON.parse(JSON.stringify(jsonData)) : null;
      processedData.value = Array.isArray(processed?.result) ? processed.result : []

      allAnnotations = []
      for (let issue of processedData.value) {

        if (issue.severity === 'high') {
          issue.level = 'error'
        } else if (issue.severity === 'medium') {
          issue.level = 'warning'
        } else {
          issue.level = 'suggestion'
        }

        for (let problemPage of issue.problem_pages) {
          if (problemPage.url === activeDeviceRender.page?.url) {
            for (let occurrence of problemPage.occurrences){
              allAnnotations.push(occurrence)
              matchedIssues.value.push({
                id: occurrence.id,
                label: issue.label,
                category: issue.category,
                suggestion: issue.suggestion_fix,
                summary: issue.summary,
                primaryPage: issue.primary_page,

              })
            }
          }
        }

      }
      setAnnotations(jsonData !== oldJsonData)

      if (['completed', 'error'].indexOf(status.value) !== -1) {
        aiProgress.value = 100
      }

      if (jsonData !== oldJsonData) {
        oldJsonData = jsonData;

        setTimeout(() => {
          let q = route.query
          if (!q['i']) {
            if (filteredIssues.value.length > 0) {
              q['i'] = filteredIssues.value[0].i;
            }
          }

          router.push({query: q, force: true});
          highlightSelectedFeedback(false)
        }, 0)
      }
      issueFilter.value = 'all'

    },
    {
      immediate: true,
    }
);

const filteredAnnotations = computed(() => {

  let indexes = filteredIssues.value.map((v) => v.i)
  return annotations.value.filter((v) => v && indexes.indexOf(v.i) !== -1)
})
let boxes = []

function showBoxes(scroll = true) {

  boxes = []
  let query = {}
  let index = 0;
  let ratio = props.devicePreview.ratio();

  for (let annotation of filteredAnnotations.value) {
    console.log(annotation.id)
    index = annotation.id;
    let q = {}
    Object.assign(q, query);
    q['i'] = index;
    q['section'] = route.query.section;
    q['s'] = route.query.s;
    let id = `highlight-box-ai-${annotation['id']}`

    let color = 'red'
    let sentiment = 'negative'
    let issue = processedData.value.find((issue) => issue.i === annotation.i)
    if (issue.level === 'warning') {
      color = 'orange'
      sentiment = 'warning'
    } else if (issue.level === 'suggestion') {
      color = 'gray'
      sentiment = 'suggestion'
    }

    let annotation_boxes = [annotation.box]
    let lineBox = {}

    let boxIndex = 0;
    for (let annotation_box of annotation_boxes) {
      boxIndex += 1;
      q = Object.assign({}, q);
      q['b'] = boxIndex
      boxes.push({
        'id': id,
        'key': `${id}-${route.query.s}-${issueFilter.value}`,
        'i': `${annotation.id}`,
        'b': boxIndex,
        'issueId': annotation.id,
        'box': annotation_box,
        'context': 'dffd',
        'highlight': false,
        'level': '',
        'active': route.query.i === `${index}`,
        'visible': true,
        'query': q,
        'style': {
          'visibility': annotation.default ? 'hidden' : 'visible',
          'left': `${annotation_box.left * ratio}px`,
          'top': `${annotation_box.top * ratio}px`,
          'width': `${annotation_box.width * ratio}px`,
          'height': `${annotation_box.height * ratio}px`,
        },
        'line': {
          'show': boxIndex === 2,
          'box': lineBox,
        },
        'variant': {
          'type': 'rough',
          'style': annotation.default ? 'circle' : 'box',
          'sentiment': sentiment,
          'color': color,
        }
      })
    }
  }
  props.devicePreview.showBoxes(boxes)
  if (scroll) {
    if (!route.query.scroll) {
      let box = boxes.find((b) => b.issueId == route.query.i)
      if (route.query.b) {
        box = boxes.find((b) => b.issueId == route.query.i && b.b == route.query.b)
      }
      if (box) {
        props.devicePreview.scrollToBox(box.box)
      }
    }
  }
}

function highlightSelectedFeedback(scroll = true) {
  let feedbackElement = document.getElementById('feedback');
  if (feedbackElement) {
    let aItems = feedbackElement.querySelectorAll('li');

    // Loop through each element and remove the class
    aItems.forEach(function (li) {
      li.classList.remove('selected');
    });

    let activeElement = feedbackElement.querySelector(`a[href="?section=${route.query.section}&i=${route.query.i}"]`);
    if (activeElement) {
      let liElement = activeElement.parentElement;
      if (liElement.nodeName !== 'LI') {
        liElement = liElement.parentElement;
      }
      liElement.classList.add('selected')
      if (scroll) {
        setTimeout(() => {
          liElement.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
        }, 1);
      }
    } else {
      let activeElement = feedbackElement.querySelector(`li[data-id="${route.query.i}"]`);
      if (activeElement) {
        activeElement.classList.add('selected')
      }
    }
  }
}


watch(
    () => props.devicePreview.mouseOverIndex,
    (newVal, oldVal) => {


      for (let issue of processedData.value) {
        issue.hover = false;
      }

      let issue = (getIssueFromBoxId(props.devicePreview.mouseOverIndex))

      if (issue) {
        issue.hover = true;
      }
    },
    {
      immediate: true
    });

watch(() => route.query, (query) => {
  if (['consistency'].includes(query.section)) {
    showBoxes()
    highlightSelectedFeedback()
    openIssue(route.query.i)
  }
})

watch(() => route.params, (newp, oldp) => {
  if (newp.deviceId !== oldp.deviceId) {
    annotations.value = []
    showBoxes()
    setAnnotations()
  }
})

// ----------

function getIssueFromBoxId(boxId) {
  // let box = boxes.find((b) => b.i == boxId)
  // if (!box) {
  //   return false;
  // }
  console.log(boxId)
  return processedData.value.find((issue) => issue.id === boxId)
}

const screenshotReady = computed(() => !!activeDeviceRender.devicePreview.screenshotFull.url)

const filteredIssues = computed(() => {
  return processedData.value;
})

const isOpen = ref(true)


onMounted(() => {

  const el = collapseRef.value
  if (!el) return

  const onShown = (e) => {
    if (e.target !== el) return
    isOpen.value = true;
    showBoxes()
  }
  const onHidden = (e) => {
    if (e.target !== el) return
    isOpen.value = false;
  }

  el.addEventListener('shown.bs.collapse', onShown)
  el.addEventListener('hidden.bs.collapse', onHidden)

  onBeforeUnmount(() => {
    el.removeEventListener('shown.bs.collapse', onShown)
    el.removeEventListener('hidden.bs.collapse', onHidden)
  })
})

function toggleAccordion() {
  const q = {...route.query}
  if (isOpen.value) {
    delete q['i']
  }
  router.replace({query: q})
}

function toggleProofreadingAccordion(item) {
  const q = {...route.query}
  let box = boxes.find((a) => a.issueId === item.i)
  // console.log(boxes)
  // if (box) {
    q['i'] = item.id
    q['b'] = 1;
    router.replace({query: q})
  // }
}

function openIssue(issueId) {
  // let issue = getIssueFromBoxId()
  const id = `details_copy_${issueId}`;
  const el = document.getElementById(id);
  if (!el) return;

  // Avoid auto-toggling on init; we’ll control it with show()
  const collapse = bootstrap.Collapse.getOrCreateInstance(el, {toggle: false});
  collapse.show();
}

async function ignoreIssue(issue) {
  const userStore = useUserStore();
  if (userStore.user.role === 'guest') {
    userStore.showNotification('login')
    return
  }

  let q = {};
  Object.assign(q, route.query);
  q['i'] = null
  await router.push({query: q})
  // issue['category'] = 'proofread.'+issue['error_type']
  let normalizedIssue = Object.assign({}, issue);
  normalizedIssue['category'] = 'proofread.' + issue['category']

  issueFilter.value = 'loading'
  showBoxes(false)
  await websiteStore.ignoreIssue(normalizedIssue, activeDeviceRender.page.externalId)

}

</script>

<template>
  <div class="p-2 mt-3">
    <cross-reference-info-box></cross-reference-info-box>
    <template v-if="aiProgress === 100">

      <ul class="list-group list-group-flush ">
        <div class="list-group-item rounded pt-0 mt-2">
          <div class="text-center p-3" v-if="issueFilter === 'loading'">
            <spinner></spinner>
          </div>

          <div class="list-group" :id="issuesAccordionId">
            <div class="list-group-item" v-for="issue in matchedIssues">

              <div :class="{'issue-header-hover': issue.hover}">
                <div class="issue-header collapsed ps-2" data-bs-toggle="collapse"
                     :data-bs-target="`#details_copy_${issue.id}`"

                     aria-expanded="false" @click="toggleProofreadingAccordion(issue)">
                  <div class="d-flex align-items-center flex-grow-1 issue-text text-truncate">
                    <span class="fa fa-square me-1 issue-icon-fixed-width" :class="{[`proofreading-text-${issue.level}`]: 1}"></span>
                    <strong class="issue-type-header">{{ issue.label }}:</strong>
                  </div>
                  <i class="fa-solid fa-chevron-right chevron"></i>
                </div>
              </div>
              <div :id="`details_copy_${issue.id}`" class="collapse" :data-bs-parent="`#${issuesAccordionId}`">
                <div class="details ps-4">
                  <p>
                    <div v-if="1" class="dropdown dropdown-menu-sm-end"
                         style="float:right;top:-2px;margin-right: -6px;">
                      <a href="#" class="text-secondary dropdown-toggle" role="button" data-bs-toggle="dropdown"
                         aria-expanded="false" title="Options" aria-label="Options menu">
                        <span class="fa fa-gear text-muted"></span>
                      </a>
                      <ul class="dropdown-menu dropdown-menu-end dropdown-menu-md text-small">
                        <li>
                          <a class="dropdown-item" href="" @click.prevent="ignoreIssue(issue)">
                            <span class="fa fa-minus-circle me-2"></span> Ignore this Issue.</a>
                        </li>

                      </ul>
                    </div>
                  </p>
                  <p>

                    <span>{{ issue.summary }}</span>
                  </p>

                </div>

              </div>
            </div>


          </div>

        </div>
      </ul>
    </template>
    <div v-else
         style="font-size: 0.875em">
      <div v-if="screenshotReady">
        <div class="text-muted m-3">
          <span class="fa fa-spinner fa-spin me-1"></span>
          Inspecting page, please wait..
        </div>
      </div>
      <div v-else>

        <div class="alert alert-danger" role="alert"
             v-if="!activeDeviceRender.page.httpStatusOkOrPending">
          <p class="mb-0"><b>Proofreading not available.</b><br> We could not load this page, the server responded
            with a status
            code of {{ activeDeviceRender.page.httpStatusCode }}. </p>
        </div>

        <template v-else>
          <div class="text-muted m-3">
            <span class="fa fa-spinner fa-spin me-1"></span>
            Waiting for page..
          </div>
        </template>
      </div>
      <div v-if="aiProgress > 99.3 && status === 'new'" class="text-danger p-1">Insights are taking longer than
        expected, <a href="">click here to retry</a>.
      </div>

    </div>

  </div>
</template>

<style scoped>
.issue-icon-fixed-width {
  width: 20px;
  display: inline-block;
  text-align: center;
}
</style>